import type { FC, ReactNode } from 'react'
import type { GuideSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight, BookOpen } from 'lucide-react'

import { cn } from '@/lib/utils'
import { getHubTheme, getHubName } from '@/constants/hubs'

interface GuideCardProps {
    guide: GuideSummary
    chapterCount?: number
    className?: string
}

export const GuideCard: FC<GuideCardProps> = ({ guide, chapterCount, className }): ReactNode => {
    const theme = getHubTheme(guide.hub)
    const Icon = theme.icon

    return (
        <article
            className={cn(
                'group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-shadow hover:shadow-md',
                className
            )}
        >
            <div className='relative aspect-[16/9] overflow-hidden bg-muted'>
                {guide.coverImage ? (
                    <img
                        src={guide.coverImage}
                        alt={guide.coverImageAlt ?? ''}
                        loading='lazy'
                        className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]'
                    />
                ) : (
                    <div className={cn('flex h-full w-full items-center justify-center', theme.softBg)}>
                        <Icon className={cn('size-10', theme.text)} aria-hidden='true' />
                    </div>
                )}
            </div>

            <div className='flex flex-1 flex-col p-6'>
                <div className='flex flex-wrap items-center gap-2'>
                    <span
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
                            theme.softBg,
                            theme.ink
                        )}
                    >
                        <BookOpen className='size-3.5' aria-hidden='true' />
                        Complete guide
                    </span>
                    <span className='text-xs font-medium text-muted-foreground'>
                        {getHubName(guide.hub)}
                    </span>
                </div>

                <h3 className='mt-3 font-heading text-xl font-bold leading-snug tracking-tight'>
                    <Link
                        href={`/guides/${guide.slug}`}
                        className='transition-colors after:absolute after:inset-0 hover:text-primary'
                        data-mdlfy-cms={`guides:${guide.id}:title`}
                    >
                        {guide.title}
                    </Link>
                </h3>

                <p
                    className='mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground'
                    data-mdlfy-cms={`guides:${guide.id}:standfirst`}
                >
                    {guide.standfirst}
                </p>

                <div className='mt-5 flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground'>
                    <span>
                        <span>{guide.readMinutes}</span> min read
                        {typeof chapterCount === 'number' && chapterCount > 0 && (
                            <>
                                {' · '}
                                <span>{chapterCount}</span> linked articles
                            </>
                        )}
                    </span>
                    <span className='inline-flex items-center gap-1 font-semibold text-primary'>
                        Read guide
                        <ArrowRight className='size-3.5' aria-hidden='true' />
                    </span>
                </div>
            </div>
        </article>
    )
}
